import { useState, useEffect } from "react";

/**
 * NotificationSettings - A small component to enable/disable notifications
 */
export default function NotificationSettings() {
  const [permission, setPermission] = useState("default");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!("Notification" in window)) {
      setPermission("denied");
      return;
    }
    setPermission(Notification.permission);
    setEnabled(Notification.permission === "granted");
  }, []);

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      alert("Notifications are not supported in this browser.");
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setEnabled(result === "granted");

      if (result === "granted") {
        // Show a test notification
        new Notification("3D Color Time Tracker", {
          body: "Notifications enabled! You'll be notified of session events.",
          icon: "/icon.svg",
        });
      }
    } catch (err) {
      console.error("Failed to request notification permission:", err);
    }
  };

  if (permission === "denied") {
    return (
      <button
        disabled
        className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-500 cursor-not-allowed"
        title="Notifications blocked by browser"
      >
        Notifications Blocked
      </button>
    );
  }

  if (enabled) {
    return (
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-green-500"></span>
        <span className="text-xs text-gray-700">Notifications On</span>
      </div>
    );
  }

  return (
    <button
      onClick={requestPermission}
      className="px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs text-gray-900 transition-colors"
    >
      Enable Notifications
    </button>
  );
}

/**
 * showSessionNotification - Utility function to show session notifications
 * Can be called from anywhere in the app
 */
export function showSessionNotification(type, data = {}) {
  if (!("Notification" in window) || Notification.permission !== "granted") {
    return null;
  }

  const notifications = {
    start: {
      title: "Session Started",
      body: `Job ${data.jobNumber}: ${data.jobName || "Session started"}`,
      tag: `session-start-${data.jobNumber}`,
    },
    pause: {
      title: "Session Paused",
      body: `Job ${data.jobNumber} paused${data.elapsed ? ` after ${formatElapsed(data.elapsed)}` : ""}`,
      tag: `session-pause-${data.jobNumber}`,
    },
    resume: {
      title: "Session Resumed",
      body: `Job ${data.jobNumber} resumed`,
      tag: `session-resume-${data.jobNumber}`,
    },
    complete: {
      title: "Session Completed",
      body: `Job ${data.jobNumber} completed${data.totalTime ? ` - Total: ${formatElapsed(data.totalTime)}` : ""}`,
      tag: `session-complete-${data.jobNumber}`,
      requireInteraction: true,
    },
    sync: {
      title: data.success ? "Sync Complete" : "Sync Failed",
      body: data.success
        ? `${data.count} session${data.count !== 1 ? "s" : ""} synced to JobBOSS`
        : "Failed to sync sessions. Will retry automatically.",
      tag: "sync-status",
    },
    longSession: {
      title: "Long Session Alert",
      body: `Job ${data.jobNumber} has been running for ${formatElapsed(data.duration)}`,
      tag: `long-session-${data.jobNumber}`,
      requireInteraction: true,
    },
  };

  const config = notifications[type];
  if (!config) return null;

  try {
    const notification = new Notification(config.title, {
      body: config.body,
      icon: "/icon.svg",
      badge: "/icon.svg",
      tag: config.tag,
      renotify: true,
      silent: false,
      requireInteraction: config.requireInteraction || false,
    });

    // Auto-close after 5 seconds if not interactive
    if (!config.requireInteraction) {
      setTimeout(() => notification.close(), 5000);
    }

    return notification;
  } catch (err) {
    console.error("Failed to show notification:", err);
    return null;
  }
}

function formatElapsed(ms) {
  if (!ms) return "0m";
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
